import { ReceiptDocument } from '~Hooks/Entities/useReceipt/document';
import { binarise } from './bitmap';
import { loadReceiptFonts, receiptFontStack } from './fonts';
import { ADYEN_PAPER_WIDTH_DOTS, MAX_SEGMENT_HEIGHT_DOTS } from './layout';
import { Painter, TextStyle } from './painter';
import { Base64Png } from './render';

/**
 * The receipt design for Adyen terminal printers (58 mm paper, 384 dots, 203 dpi).
 *
 * Design intent:
 *  - One clear hierarchy: order number and total are the two things a customer looks for,
 *    so they are the largest elements on the paper.
 *  - Real columns. Quantities, titles and amounts sit in their own columns and long titles
 *    wrap instead of being truncated to "Cappu...".
 *  - Copies, refunds and pre-bills carry an inverted badge that cannot be missed or faked
 *    by tearing off a line.
 *  - Everything is set in the brand font at weights that survive thermal printing; no
 *    regular or light weights, no grey, no hairlines thinner than two dots.
 */

/** Render at this multiple of the paper resolution before reducing to 1-bit. */
const SUPERSAMPLE = 2;

/** Side margin in dots; thermal heads are unreliable on the outermost dots. */
const MARGIN = 14;

/** Type scale (dots). At 203 dpi, 20 dots is roughly 2.5 mm: comfortable body size. */
const T = {
  display: { size: 64, weight: 700, align: 'center', leading: 1.1 } as TextStyle,
  title: {
    size: 34,
    weight: 700,
    align: 'center',
    uppercase: true,
    letterSpacing: 3,
  } as TextStyle,
  heading: { size: 26, weight: 700, align: 'center' } as TextStyle,
  total: { size: 32, weight: 700 } as TextStyle,
  totalLabel: { size: 24, weight: 700 } as TextStyle,
  body: { size: 20, weight: 600 } as TextStyle,
  bodyStrong: { size: 20, weight: 700 } as TextStyle,
  bodyCenter: { size: 20, weight: 600, align: 'center' } as TextStyle,
  small: { size: 17, weight: 600 } as TextStyle,
  smallStrong: { size: 17, weight: 700 } as TextStyle,
  smallCenter: { size: 17, weight: 600, align: 'center' } as TextStyle,
  label: { size: 15, weight: 700, uppercase: true, letterSpacing: 1.5 } as TextStyle,
  badge: { size: 18, weight: 700, uppercase: true, letterSpacing: 1.5 } as TextStyle,
};

const loadImage = (src: string) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to load receipt logo'));
    img.src = src;
  });

const createCanvas = (width: number, height: number) => {
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(width));
  canvas.height = Math.max(1, Math.round(height));

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Could not get 2d canvas context');
  }

  return { canvas, ctx };
};

/** A small uppercase section label with a rule under it. */
const sectionLabel = (p: Painter, text: string) => {
  p.text(text, T.label);
  p.space(4);
  p.rule(2);
  p.space(8);
};

/** Lays the document out on the painter. Pure layout, no pixels yet. */
export const layoutReceipt = (p: Painter, doc: ReceiptDocument, logo?: HTMLImageElement) => {
  const w = p.contentWidth;

  // Header: logo, order number, venue
  if (logo) {
    p.space(6);
    p.image(logo, logo.width, logo.height, Math.round(w * 0.85), 160);
    p.space(14);
  } else {
    p.space(10);
  }

  if (doc.orderNumber) {
    p.text(doc.orderNumber, T.display);
    p.space(10);
  }

  doc.introLines.forEach((line) => p.text(line, T.bodyCenter));
  if (doc.introLines.length) {
    p.space(8);
  }

  if (doc.merchant.venueName) {
    p.text(doc.merchant.venueName, T.heading);
  }
  if (doc.merchant.companyName) {
    p.text(doc.merchant.companyName, T.smallCenter);
  }
  if (doc.merchant.address) {
    p.text(doc.merchant.address, T.smallCenter);
  }
  const cityLine = [doc.merchant.postalCode, doc.merchant.city].filter(Boolean).join(' ');
  if (cityLine) {
    p.text(cityLine, T.smallCenter);
  }
  if (doc.merchant.orgNumber) {
    p.text(`${doc.merchant.vatNumberLabel}: ${doc.merchant.orgNumber}`, T.smallCenter);
  }
  p.space(16);
  p.cut();

  // Title block
  if (doc.badge) {
    p.badge(doc.badge, T.badge);
    p.space(8);
  }
  p.text(doc.title, T.title);
  p.space(10);
  p.rule(3);
  p.space(10);

  // Spanish customer block
  if (doc.customer.length) {
    doc.customer.forEach((line) => p.text(line, T.bodyCenter));
    p.space(8);
    p.rule(2, { dashed: true });
    p.space(8);
  }

  // Meta rows (receipt number, date, table...)
  doc.meta.forEach((entry) => {
    p.row(entry.label, entry.value, T.body, T.bodyStrong);
  });
  if (doc.meta.length) {
    p.space(10);
    p.rule(2, { dashed: true });
    p.space(10);
  }
  p.cut();

  // Items
  const QTY_COL = 34;
  const GAP = 10;
  doc.items.forEach((item) => {
    const qty = `${item.quantity}`;
    const totalWidth = p.measure(item.total, T.bodyStrong);
    const titleMax = w - QTY_COL - GAP - totalWidth - GAP;
    const titleLines = p.wrap(item.title, T.body, titleMax);
    const top = p.height;

    // Quantity column, right aligned so 1 and 12 line up; title next to it; total on the right.
    // The three blocks share the same top, so the cursor is rewound between them.
    p.columns([{ text: qty, width: QTY_COL, align: 'right' }], T.bodyStrong);
    const afterQty = p.height;
    p.moveTo(top);
    titleLines.forEach((line) =>
      p.text(line, T.body, { indent: QTY_COL + GAP, maxWidth: titleMax }),
    );
    const afterTitle = p.height;
    p.moveTo(top);
    p.text(item.total, { ...T.bodyStrong, align: 'right' });
    p.moveTo(Math.max(afterQty, afterTitle, p.height));

    if (item.unitPrice) {
      p.text(`${item.quantity} × ${item.unitPrice}`, T.small, { indent: QTY_COL + GAP });
    }
    item.variations.forEach((variation) => {
      p.text(`+ ${variation}`, T.small, { indent: QTY_COL + GAP });
    });
    if (item.discount) {
      p.row(item.discountTitle || '', `-${item.discount}`, T.small, T.smallStrong, {
        indent: QTY_COL + GAP,
      });
    }
    p.space(6);
  });

  if (doc.items.length) {
    p.space(4);
    p.rule(2);
    p.space(10);
  }
  p.cut();

  // Totals
  if (doc.totals) {
    if (doc.totals.discount) {
      p.row(doc.totals.discountLabel, `-${doc.totals.discount}`, T.body, T.bodyStrong);
      p.space(4);
    }
    p.row(
      `${doc.totals.label} ${doc.totals.currency}`.trim(),
      doc.totals.total,
      T.totalLabel,
      T.total,
    );
    p.space(12);
  }

  if (doc.tipRefunded) {
    p.row(doc.tipRefunded.label, doc.tipRefunded.value, T.body, T.bodyStrong);
    p.space(12);
  }

  if (doc.tipSuggestions) {
    sectionLabel(p, doc.tipSuggestions.title);
    doc.tipSuggestions.rows.forEach((row) => p.row(row.label, row.value, T.body, T.bodyStrong));
    p.space(12);
  }
  p.cut();

  // VAT table
  if (doc.vat && doc.vat.rows.length) {
    const cols = [Math.round(w * 0.2), Math.round(w * 0.28), Math.round(w * 0.27)];
    const last = w - cols[0] - cols[1] - cols[2];
    const widths = [...cols, last];
    const aligns: Array<'left' | 'right'> = ['left', 'right', 'right', 'right'];

    p.columns(
      doc.vat.columns.map((text, i) => ({ text, width: widths[i], align: aligns[i] })),
      { ...T.label, letterSpacing: 0.5 },
    );
    p.space(3);
    p.rule(2);
    p.space(4);
    doc.vat.rows.forEach((row) => {
      p.columns(
        row.map((text, i) => ({ text, width: widths[i], align: aligns[i] })),
        T.small,
      );
    });
    p.space(14);
  }
  p.cut();

  // Payments and card
  if (doc.payments.length) {
    sectionLabel(p, doc.paymentsTitle);

    doc.payments.forEach((payment) => {
      p.row(payment.method, payment.amount, T.body, T.bodyStrong);
      if (payment.tipNote) {
        p.text(payment.tipNote, T.small);
      }
    });

    if (doc.tipTotal) {
      p.space(4);
      p.row(doc.tipTotal.label, doc.tipTotal.value, T.body, T.bodyStrong);
    }
    if (doc.totalIncTip) {
      p.row(doc.totalIncTip.label, doc.totalIncTip.value, T.bodyStrong, T.bodyStrong);
    }

    if (doc.card) {
      p.space(8);
      const cardLine = [doc.card.brand, doc.card.maskedPan].filter(Boolean).join(' ');
      if (cardLine || doc.card.entryMode) {
        p.row(cardLine, doc.card.entryMode ?? '', T.bodyStrong, T.small);
      }
      if (doc.card.details.length) {
        p.space(2);
        p.text(doc.card.details.map((d) => `${d.label} ${d.value}`).join('   '), {
          ...T.small,
          size: 15,
        });
      }
    }
    p.space(14);
  }
  p.cut();

  // Invoice form
  if (doc.invoiceForm) {
    p.rule(2, { dashed: true });
    p.space(10);
    doc.invoiceForm.forEach((field) => {
      p.row(field.label, field.value, T.small, T.smallStrong);
      p.space(20);
      p.rule(1);
      p.space(10);
    });
    p.space(6);
  }
  p.cut();

  // Legal and footer
  if (doc.legal.length) {
    p.rule(2, { dashed: true });
    p.space(8);
    doc.legal.forEach((entry) => p.row(entry.label, entry.value, T.small, T.small));
    p.space(10);
  }

  if (doc.bottomLines.length) {
    p.space(6);
    doc.bottomLines.forEach((line) => p.text(line, T.bodyCenter));
  }

  if (doc.staffLine) {
    p.space(6);
    p.text(doc.staffLine, T.smallCenter);
  }

  p.space(24);
};

/**
 * Renders a receipt document into one or more 384 px wide, 1-bit PNGs (bare base64).
 * The images must be printed in order; each is a contiguous slice of the receipt.
 */
export const renderReceiptDocument = async (
  doc: ReceiptDocument,
  logoDataUrl?: string,
): Promise<Base64Png[]> => {
  const brandFont = await loadReceiptFonts();
  const fontFamily = receiptFontStack(brandFont);

  let logo: HTMLImageElement | undefined;
  if (logoDataUrl) {
    try {
      logo = await loadImage(logoDataUrl);
    } catch (e) {
      logo = undefined;
    }
  }

  // Scratch context for measuring at the same scale we will draw at
  const { ctx: measureCtx } = createCanvas(8, 8);
  measureCtx.scale(SUPERSAMPLE, SUPERSAMPLE);

  const painter = new Painter(ADYEN_PAPER_WIDTH_DOTS, MARGIN, fontFamily, measureCtx);
  layoutReceipt(painter, doc, logo);

  const segments = painter.segments(MAX_SEGMENT_HEIGHT_DOTS);
  const images: Base64Png[] = [];

  for (const segment of segments) {
    const height = segment.end - segment.start;
    const { ctx } = createCanvas(ADYEN_PAPER_WIDTH_DOTS * SUPERSAMPLE, height * SUPERSAMPLE);

    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);
    ctx.scale(SUPERSAMPLE, SUPERSAMPLE);
    painter.paint(ctx, segment.start);

    const source = ctx.getImageData(0, 0, ctx.canvas.width, ctx.canvas.height);
    const { canvas: out, ctx: outCtx } = createCanvas(ADYEN_PAPER_WIDTH_DOTS, height);
    const target = outCtx.createImageData(ADYEN_PAPER_WIDTH_DOTS, height);

    binarise({
      source,
      supersample: SUPERSAMPLE,
      target,
      ditherRects: painter.imageRects.map((r) => ({ ...r, y: r.y - segment.start })),
    });

    outCtx.putImageData(target, 0, 0);
    images.push(out.toDataURL('image/png').replace(/^data:image\/png;base64,/, ''));
  }

  return images;
};
